import { Component, inject, OnInit, PLATFORM_ID, DestroyRef, DOCUMENT } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LocalStorageService } from 'src/app/shared/services/local-storage.service';
import { LOCAL_STORAGE_KEYS } from 'src/app/shared/constants/local-storage.constants';
import { Store } from '@ngxs/store';
import { GetUserRegions } from 'src/app/shared/store/states/user/user.actions';
import { combineLatest, switchMap } from 'rxjs';
import { UserState } from 'src/app/shared/store/states/user/user.state';
import { map, tap } from 'rxjs/operators';
import { datadogLogs } from '@datadog/browser-logs';
import { environment } from 'src/environments/environment';
import { TranslocoService } from '@jsverse/transloco';
import { translocoConfig } from './transloco/transloco.config';
import { RegionDto } from 'src/app/shared/dtos/user.dto';
import { AppcoreService } from './shared/services/appcore';
import { RegionModal } from 'src/app/core/components/modals/region-modal/region-modal';
import { TermsAndConditionsModal } from 'src/app/core/components/modals/terms-and-conditions-modal/terms-and-conditions-modal';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet],
  providers: [DialogService],
  templateUrl: './app.html',
  styleUrl: './app.scss'
})
export class App implements OnInit {
  private readonly store = inject(Store);
  private readonly dialogService = inject(DialogService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);
  private transloco = inject(TranslocoService);
  private readonly appcoreService = inject(AppcoreService);

  private ref: DynamicDialogRef | null;
  private currentRegion: RegionDto | undefined;

  ngOnInit(): void {
    datadogLogs.init({
      clientToken: '',
      site: 'datadoghq.eu',
      forwardErrorsToLogs: true,
      sessionSampleRate: 100,
      trackingConsent: 'not-granted'
    });

    datadogLogs.setGlobalContext({
      env: environment.environmentName
    });

    if (isPlatformBrowser(this.platformId)) {
      this.initializeGoogleAnalytics();

      const termsAccepted = this.localStorageService.getItem(LOCAL_STORAGE_KEYS.TermsAndConditionsAccepted);

      combineLatest([this.appcoreService.isAuthenticated(), this.appcoreService.isAuthorised()]).pipe(
        takeUntilDestroyed(this.destroyRef),
        map(([isAuthenitcated, isAuthorised]) => {
          const canProceedModals = isAuthenitcated && isAuthorised;

          if (!termsAccepted && canProceedModals) {
            this.openTermsAndConditionsDialog();
            return;
          }
          if (canProceedModals) {
            this.getRegions();
          }
        })
      ).subscribe();
    }
  }

  private initializeGoogleAnalytics(): void {
    if (!environment.googleAnalyticsId) {
      return;
    }

    const gtagScript = this.document.createElement('script');
    gtagScript.async = true;
    gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${environment.googleAnalyticsId}`;
    this.document.head.appendChild(gtagScript);

    const gtagInitScript = this.document.createElement('script');
    gtagInitScript.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${environment.googleAnalyticsId}');
    `;
    this.document.head.appendChild(gtagInitScript);
  }


  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      this.setLangFromBrowser();
      window.addEventListener('languagechange', () => {
        this.setLangFromBrowser();
      });
    }
  }

  private setLangFromBrowser() {
      const browserLang = (navigator.language || navigator.languages?.[0] || '')
        .toLowerCase()
        .split('-')[0];
      const lang = (translocoConfig.availableLangs.includes(browserLang!) ? browserLang! : translocoConfig.defaultLang);
      this.transloco.setActiveLang(lang);
  }

  private getRegions() {
    const initialRegion = this.localStorageService.getItem<RegionDto>(LOCAL_STORAGE_KEYS.InitialRegion);

    this.store.dispatch(new GetUserRegions()).pipe(
      takeUntilDestroyed(this.destroyRef),
      switchMap(() => this.store.select(UserState.getUserRegions)),
      tap(regions => {
        // If region is valid, no need to show region modal, otherwise force user to pick one
        if (initialRegion?.code && regions.some(r => r.code === initialRegion.code)) {
          return;
        }
        
        if (regions && regions.length > 1) {
          this.currentRegion = regions.find(region => region.current);
          this.openRegionDialog();
        }
      })
    ).subscribe();
  }

  private openTermsAndConditionsDialog(): void {
    this.ref = this.dialogService.open(TermsAndConditionsModal, {
      header: this.transloco.translate('settings.termsAndConditions'),
      focusOnShow: false,
      closable: false,
      modal: true,
      width: '50%',
      breakpoints: {
        '1400px': '50%',
        '960px': '75%',
        '640px': '90%'
      },
    });

    this.ref?.onClose.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.localStorageService.setItem(LOCAL_STORAGE_KEYS.TermsAndConditionsAccepted, true);
      this.getRegions();
    })
  }

  private openRegionDialog(): void {
    this.ref = this.dialogService.open(RegionModal, {
      header: this.transloco.translate('settings.selectRegion'),
      focusOnShow: false,
      closable: false,
      modal: true,
      width: '30%',
      breakpoints: {
        '1400px': '50%',
        '960px': '75%',
        '640px': '90%'
      },
    });

    this.ref?.onClose.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      if (!this.currentRegion) {
        return;
      }
      
      this.localStorageService.setItem(LOCAL_STORAGE_KEYS.InitialRegion, this.currentRegion);
    })
  }
}
